import { useEffect, useState } from "react";
import api, { BACKEND_URL, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, Copy, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function CalendarSubscribeButton({ isDark }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/ical/token");
      setToken(data.token);
    } catch {}
  };
  useEffect(() => { if (open && !token) load(); }, [open]);

  const url = token ? `${BACKEND_URL}/api/ical/${token}.ics` : "";
  const webcal = url.replace(/^https?:\/\//, "webcal://");

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); }
    catch { toast.error("Could not copy"); }
  };

  const rotate = async () => {
    if (!window.confirm("Generate a new link? The old link will stop working.")) return;
    try {
      const { data } = await api.post("/ical/rotate");
      setToken(data.token);
      toast.success("New calendar link generated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ical-subscribe-btn"
        aria-label="Subscribe to my calendar feed"
        className={`p-2 rounded transition-colors ${isDark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-black/60 hover:text-black hover:bg-black/5"}`}
      >
        <Calendar className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Sync to your calendar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm opacity-70">Add this feed to Apple Calendar, Google Calendar or Outlook — every booking (and any future ones) will appear automatically.</p>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-2">One-click add (Apple / Outlook)</div>
              <div className="flex gap-2">
                <a href={webcal} className="btn-volt px-4 py-2.5 rounded-md inline-flex items-center gap-2 text-sm" data-testid="ical-webcal-link">
                  <ExternalLink className="w-3.5 h-3.5" /> Open in calendar app
                </a>
                <button onClick={()=>copy(webcal)} className="px-4 py-2.5 rounded-md border border-border hover:border-[#83B68B] inline-flex items-center gap-2 text-sm">
                  <Copy className="w-3.5 h-3.5" /> Copy webcal://
                </button>
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-2">Google Calendar (paste URL)</div>
              <div className="flex gap-2 items-stretch">
                <div className="flex-1 bg-muted border border-border rounded px-3 py-2.5 text-xs font-mono break-all">{url || "Loading…"}</div>
                <button onClick={()=>copy(url)} data-testid="ical-copy-url-btn" className="px-4 py-2.5 rounded-md border border-border hover:border-[#83B68B] inline-flex items-center gap-2 text-sm shrink-0">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <p className="text-xs opacity-60 mt-2">In Google Calendar: Other calendars → + → From URL → paste this link.</p>
            </div>

            <div className="pt-3 border-t border-border">
              <button onClick={rotate} className="text-xs tracking-widest uppercase opacity-60 hover:opacity-100 inline-flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" /> Rotate link (revoke old one)
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
